import { createRoute, Link, useNavigate } from '@tanstack/react-router'
import { projectRoute } from '../route'
import { useState, useEffect, useMemo, useRef } from 'react'
import { Pencil, PencilLine, Plus, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { orpc } from '@/lib/orpc-client'
import { forgetDrawingCache } from '@/lib/drawings/drawing-client'
import { useDateLocale } from '@/i18n/dates'

export const drawIndexRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/draw/',
  component: DrawingsListPage,
})

type Drawing = Awaited<ReturnType<typeof orpc.drawings.list>>['items'][number]

function DrawingsListPage() {
  const { projectId } = drawIndexRoute.useParams()
  const navigate = useNavigate()
  const { t } = useTranslation('documents')
  const { intl } = useDateLocale()
  const [drawings, setDrawings] = useState<Drawing[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isCreating, setIsCreating] = useState(false)
  const [sortBy, setSortBy] = useState<'updated' | 'name'>('updated')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editingTitle, setEditingTitle] = useState('')
  const editInputRef = useRef<HTMLInputElement>(null)
  const [deleteTarget, setDeleteTarget] = useState<Drawing | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const sortedDrawings = useMemo(() => {
    const sorted = [...drawings]
    if (sortBy === 'name') {
      sorted.sort((a, b) => a.title.localeCompare(b.title, intl))
    } else {
      sorted.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
    }
    return sorted
  }, [drawings, sortBy, intl])

  useEffect(() => {
    orpc.drawings
      .list({ projectId })
      .then((res) => setDrawings(res.items))
      .catch(() => {})
      .finally(() => setIsLoading(false))
  }, [projectId])

  const handleCreate = async () => {
    if (isCreating) return
    setIsCreating(true)
    try {
      const now = new Date()
      const dd = String(now.getDate()).padStart(2, '0')
      const mm = String(now.getMonth() + 1).padStart(2, '0')
      const drawing = await orpc.drawings.save({
        projectId,
        title: t('drawings.newTitle', { date: `${dd}/${mm}` }),
        data: null,
      })
      void navigate({
        to: '/$projectId/draw/$drawingId',
        params: { projectId, drawingId: drawing.id },
      })
    } catch {
      setIsCreating(false)
    }
  }

  const startRename = (drawing: Drawing) => {
    setEditingId(drawing.id)
    setEditingTitle(drawing.title)
    setTimeout(() => editInputRef.current?.select(), 0)
  }

  const commitRename = async () => {
    if (!editingId) return
    const trimmed = editingTitle.trim()
    if (!trimmed) {
      setEditingId(null)
      return
    }
    try {
      await orpc.drawings.save({ projectId, drawingId: editingId, title: trimmed })
      setDrawings((prev) => prev.map((d) => (d.id === editingId ? { ...d, title: trimmed } : d)))
    } catch {
      // Revert on error — title stays unchanged
    }
    setEditingId(null)
  }

  const handleDelete = async () => {
    if (!deleteTarget || isDeleting) return
    setIsDeleting(true)
    try {
      await orpc.drawings.delete({ projectId, drawingId: deleteTarget.id })
      void forgetDrawingCache(projectId, deleteTarget.id)
      setDrawings((prev) => prev.filter((d) => d.id !== deleteTarget.id))
      setDeleteTarget(null)
    } catch {
      // keep dialog open on error
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <div className="-mt-(--topbar-h) flex-1 overflow-auto p-6 pt-[calc(var(--topbar-h)+1.5rem)]">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">{t('drawings.sortLabel')}</span>
            <div className="flex rounded-md border border-border">
              <Button
                variant={sortBy === 'updated' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setSortBy('updated')}
                className="rounded-r-none border-r border-border"
              >
                {t('drawings.sortUpdated')}
              </Button>
              <Button
                variant={sortBy === 'name' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setSortBy('name')}
                className="rounded-l-none"
              >
                {t('drawings.sortName')}
              </Button>
            </div>
          </div>
          <Button onClick={handleCreate} disabled={isCreating} className="gap-2">
            <Plus className="h-4 w-4" />
            {t('drawings.create')}
          </Button>
        </div>

        {isLoading ? (
          <p className="text-sm text-muted-foreground">{t('drawings.loading')}</p>
        ) : drawings.length === 0 ? (
          <div className="text-center py-12 space-y-3">
            <Pencil className="h-12 w-12 mx-auto text-muted-foreground/40" />
            <p className="text-muted-foreground">{t('drawings.empty')}</p>
            <Button
              variant="outline"
              onClick={handleCreate}
              disabled={isCreating}
              className="gap-2"
            >
              <Plus className="h-4 w-4" />
              {t('drawings.createFirst')}
            </Button>
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {sortedDrawings.map((drawing) => (
              <Link
                key={drawing.id}
                to="/$projectId/draw/$drawingId"
                params={{ projectId, drawingId: drawing.id }}
                className="flex flex-col rounded-lg border border-border bg-card hover:bg-accent transition-colors overflow-hidden"
              >
                <div className="aspect-[4/3] bg-white flex items-center justify-center overflow-hidden">
                  {drawing.thumbnailUrl ? (
                    <img
                      src={drawing.thumbnailUrl}
                      alt={drawing.title}
                      className="w-full h-full object-contain"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none'
                        e.currentTarget.nextElementSibling?.classList.remove('hidden')
                      }}
                    />
                  ) : null}
                  <Pencil
                    className={`h-8 w-8 text-muted-foreground/40${drawing.thumbnailUrl ? ' hidden' : ''}`}
                  />
                </div>
                <div className="p-3 min-w-0">
                  {editingId === drawing.id ? (
                    <input
                      ref={editInputRef}
                      value={editingTitle}
                      onChange={(e) => setEditingTitle(e.target.value)}
                      onBlur={() => commitRename()}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') commitRename()
                        if (e.key === 'Escape') setEditingId(null)
                      }}
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                      }}
                      className="w-full font-medium text-foreground bg-transparent border-b border-primary outline-none"
                    />
                  ) : (
                    <div className="flex items-center gap-1 group/title">
                      <p className="font-medium text-foreground truncate flex-1">{drawing.title}</p>
                      <button
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          startRename(drawing)
                        }}
                        className="opacity-0 group-hover/title:opacity-100 p-0.5 rounded hover:bg-muted transition-opacity shrink-0"
                      >
                        <PencilLine className="h-3.5 w-3.5 text-muted-foreground" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          setDeleteTarget(drawing)
                        }}
                        className="opacity-0 group-hover/title:opacity-100 p-0.5 rounded hover:bg-destructive/10 transition-opacity shrink-0"
                      >
                        <Trash2 className="h-3.5 w-3.5 text-destructive" />
                      </button>
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {t('drawings.updated', {
                      date: new Date(drawing.updatedAt).toLocaleDateString(intl),
                    })}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        )}
        <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('drawings.deleteDialog.title')}</DialogTitle>
              <DialogDescription>
                {t('drawings.deleteDialog.description', { title: deleteTarget?.title ?? '' })}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={isDeleting}>
                {t('drawings.deleteDialog.cancel')}
              </Button>
              <Button variant="destructive" onClick={handleDelete} disabled={isDeleting}>
                {isDeleting
                  ? t('drawings.deleteDialog.deleting')
                  : t('drawings.deleteDialog.confirm')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  )
}
